/**
 * Cart Icon and Cart Drawer Styles
 *
 * Both surfaces are self-contained overlays (not inline content), so they
 * use opinionated tokens rather than the fully-adaptive currentColor
 * approach used by the cart page. Rationale: overlays float above the
 * host page, and users expect a consistent island-like appearance for
 * commerce UI regardless of the underlying WP theme.
 *
 * Tokens honored: merchant accent (--ks-primary chain), fonts (WP host
 * bridge when present), radii. Everything else is opinionated.
 */

import { OVERLAY_TOKENS } from '../theme';

export const CART_ICON_STYLES = `
  .ks-cart-icon {
    --kso-accent: var(--ks-primary, var(--ks-host-primary, #6366f1));
    --kso-accent-ink: var(--ks-primary-contrast, #ffffff);

    position: fixed;
    bottom: 28px;
    right: 28px;
    z-index: 99990;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--kso-accent);
    color: var(--kso-accent-ink);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
      0 2px 4px rgba(17, 24, 39, 0.06),
      0 12px 28px -8px color-mix(in srgb, var(--kso-accent) 55%, rgba(17, 24, 39, 0.45));
    transition: transform 220ms cubic-bezier(0.22, 0.61, 0.36, 1),
                box-shadow 220ms ease,
                filter 180ms ease;
  }

  .ks-cart-icon:hover {
    transform: scale(1.06);
    filter: brightness(1.08);
    box-shadow:
      0 2px 4px rgba(17, 24, 39, 0.06),
      0 16px 36px -10px color-mix(in srgb, var(--kso-accent) 65%, rgba(17, 24, 39, 0.45));
  }

  .ks-cart-icon:active { transform: scale(0.95); }

  .ks-cart-icon svg {
    width: 22px;
    height: 22px;
  }

  .ks-cart-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #111827;
    color: #ffffff;
    font-size: 0.6875rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    line-height: 1;
    border: 2px solid #ffffff;
    transition: transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .ks-cart-badge.ks-hidden { display: none; }

  .ks-cart-badge.ks-bounce { animation: ks-badge-bounce 360ms cubic-bezier(0.34, 1.56, 0.64, 1); }

  @keyframes ks-badge-bounce {
    0%, 100% { transform: scale(1); }
    40% { transform: scale(1.35); }
  }

  @media (max-width: 480px) {
    .ks-cart-icon {
      bottom: 18px;
      right: 18px;
      width: 50px;
      height: 50px;
    }
    .ks-cart-icon svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const CART_DRAWER_STYLES = `
  .ks-drawer-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 99996;
    opacity: 0;
    transition: opacity 320ms ease;
  }

  .ks-drawer-backdrop.ks-visible { opacity: 1; }

  .ks-drawer {
    ${OVERLAY_TOKENS}
    /* Drawer-specific radius override (matches storefront style language) */
    --kso-radius-md: 12px;
    --kso-radius-sm: 8px;
    --kso-radius-xs: 6px;

    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(420px, 100vw);
    background: var(--kso-surface);
    color: var(--kso-ink);
    font-family: var(--kso-font);
    z-index: 99997;
    transform: translateX(100%);
    transition: transform 380ms cubic-bezier(0.22, 0.61, 0.36, 1);
    display: flex;
    flex-direction: column;
    box-shadow: -8px 0 40px -8px rgba(17, 24, 39, 0.18);
  }

  .ks-drawer * { box-sizing: border-box; }

  .ks-drawer.ks-open { transform: translateX(0); }

  .ks-drawer-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 20px 22px 16px;
    border-bottom: 1px solid var(--kso-hairline);
    flex-shrink: 0;
  }

  .ks-drawer-title {
    font-family: var(--kso-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0;
    color: var(--kso-ink);
    letter-spacing: -0.01em;
  }

  .ks-drawer-count {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--kso-muted);
    margin-left: 8px;
    font-variant-numeric: tabular-nums;
  }

  .ks-drawer-close {
    background: none;
    border: none;
    color: var(--kso-muted);
    cursor: pointer;
    padding: 6px;
    margin: -6px;
    border-radius: 999px;
    transition: background 180ms ease, color 180ms ease;
    align-self: center;
  }

  .ks-drawer-close:hover {
    background: var(--kso-hairline);
    color: var(--kso-ink);
  }

  .ks-drawer-close svg {
    width: 18px;
    height: 18px;
    display: block;
  }

  .ks-drawer-body {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: var(--kso-hairline-strong) transparent;
  }

  /* Empty state */
  .ks-drawer-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 48px 32px;
    text-align: center;
    color: var(--kso-muted);
  }

  .ks-drawer-empty svg {
    width: 44px;
    height: 44px;
    margin-bottom: 18px;
    color: var(--kso-accent);
    opacity: 0.7;
    stroke-width: 1.25;
  }

  .ks-drawer-empty-text {
    font-family: var(--kso-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 6px;
    color: var(--kso-ink);
    letter-spacing: -0.01em;
  }

  .ks-drawer-empty-sub {
    font-size: 0.8125rem;
    color: var(--kso-muted);
    line-height: 1.5;
    max-width: 260px;
  }

  .ks-drawer-continue-btn {
    margin-top: 20px;
    padding: 10px 20px;
    background: transparent;
    border: 1px solid var(--kso-hairline-strong);
    border-radius: var(--kso-btn-radius);
    color: var(--kso-ink-soft);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 180ms ease, color 180ms ease;
  }

  .ks-drawer-continue-btn:hover {
    border-color: var(--kso-ink);
    color: var(--kso-ink);
  }

  /* Item rows */
  .ks-drawer-items { padding: 4px 0; }

  .ks-drawer-item {
    display: flex;
    gap: 14px;
    padding: 16px 22px;
    border-bottom: 1px solid var(--kso-hairline);
  }

  .ks-drawer-item:last-child { border-bottom: none; }

  .ks-drawer-item-img,
  .ks-drawer-item-placeholder {
    width: 64px;
    height: 64px;
    border-radius: var(--kso-radius-xs);
    object-fit: cover;
    background: var(--kso-surface-alt);
    flex-shrink: 0;
    border: 1px solid var(--kso-hairline);
  }

  .ks-drawer-item-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--kso-muted-2);
    border: 1px solid var(--kso-hairline);
  }

  .ks-drawer-item-placeholder svg {
    width: 22px;
    height: 22px;
    opacity: 0.5;
  }

  .ks-drawer-item-info {
    flex: 1;
    min-width: 0;
  }

  .ks-drawer-item-title {
    font-family: var(--kso-font-display);
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--kso-ink);
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: -0.005em;
  }

  .ks-drawer-item-variant {
    font-size: 0.75rem;
    color: var(--kso-muted);
    margin-bottom: 10px;
  }

  .ks-drawer-item-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .ks-drawer-item-qty {
    display: inline-flex;
    align-items: center;
    border: 1px solid var(--kso-hairline-strong);
    border-radius: 999px;
    overflow: hidden;
    height: 30px;
    background: var(--kso-surface);
  }

  .ks-drawer-qty-btn {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--kso-ink-soft);
    cursor: pointer;
    font-size: 0.9375rem;
    font-family: inherit;
    transition: background 150ms ease, color 150ms ease;
  }

  .ks-drawer-qty-btn:hover {
    background: var(--kso-surface-alt);
    color: var(--kso-ink);
  }

  .ks-drawer-qty-value {
    min-width: 24px;
    text-align: center;
    font-size: 0.8125rem;
    font-weight: 500;
    padding: 0 4px;
    color: var(--kso-ink);
    font-variant-numeric: tabular-nums;
  }

  .ks-drawer-item-price {
    font-family: var(--kso-font-display);
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--kso-ink);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .ks-drawer-item-remove {
    background: none;
    border: none;
    color: var(--kso-muted-2);
    cursor: pointer;
    padding: 6px;
    border-radius: 999px;
    transition: color 180ms ease, background 180ms ease, transform 200ms ease;
    flex-shrink: 0;
    align-self: flex-start;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .ks-drawer-item-remove:hover {
    color: var(--kso-danger);
    background: var(--kso-danger-bg);
    transform: rotate(90deg);
  }

  .ks-drawer-item-remove svg {
    width: 14px;
    height: 14px;
    stroke-width: 1.75;
  }

  /* Footer */
  .ks-drawer-footer {
    padding: 18px 22px 20px;
    border-top: 1px solid var(--kso-hairline);
    background: var(--kso-surface-alt);
    flex-shrink: 0;
  }

  .ks-drawer-summary {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 14px;
  }

  .ks-drawer-subtotal-label {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--kso-muted);
  }

  .ks-drawer-subtotal-value {
    font-family: var(--kso-font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--kso-ink);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }

  .ks-drawer-checkout-btn {
    width: 100%;
    padding: 14px 20px;
    background: var(--kso-ink);
    color: var(--kso-accent-ink);
    border: none;
    border-radius: var(--kso-btn-radius);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-family: inherit;
    cursor: pointer;
    transition: background 220ms ease, transform 120ms ease, letter-spacing 220ms ease;
  }

  .ks-drawer-checkout-btn:hover:not(:disabled) {
    background: var(--kso-accent);
    letter-spacing: 0.18em;
  }

  .ks-drawer-checkout-btn:active:not(:disabled) {
    transform: scale(0.985);
  }

  .ks-drawer-checkout-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    .ks-drawer {
      width: 100vw;
    }
  }
`;
